import Logger from "./Logger";
import { Feature } from "../../core/geo/types";
import { Layer, PresentState } from "../../rootState";
import { Geometry, LineString, Point, Polygon, Position } from "geojson";
import { reproject } from "../../../MapStore2/web/client/utils/CoordinatesUtils";
import { Projection } from "../types";
import { flattenCoordinates } from "./draw/geometry";
import { isNotEmptyArray } from "../../core/utils/array";
import { isNumber } from "lodash";
import { getZoomFromResolution } from "mapstore2/web/client/utils/MapUtils";

const TARGET_SCREEN_RATIO_MIN = 0.08;
const TARGET_SCREEN_RATIO_MAX = 0.5;
const MAX_ZOOM = 16;

const logger = new Logger("FeatureCenter");

type BordersType = {
    left: number;
    right: number;
    top: number;
    bottom: number;
};

function getCoordinatesBorders(coordinates: Position, borders: BordersType) {
    if (isNaN(coordinates[0]) || isNaN(coordinates[1])) {
        coordinates.forEach((coords: any) => getCoordinatesBorders(coords, borders));
    } else {
        if (!borders.left || borders.left > coordinates[0]) {
            borders.left = coordinates[0];
        }
        if (!borders.right || borders.right < coordinates[0]) {
            borders.right = coordinates[0];
        }
        if (!borders.top || borders.top > coordinates[1]) {
            borders.top = coordinates[1];
        }
        if (!borders.bottom || borders.bottom < coordinates[1]) {
            borders.bottom = coordinates[1];
        }
    }
    return borders;
}

function getGeometriesBorders(geometries: Geometry[]) {
    const borders: BordersType = { bottom: 0, top: 0, left: 0, right: 0 };

    const coordinates = geometries
        .map((geometry) => flattenCoordinates((geometry as LineString | Point | Polygon).coordinates))
        .reduce((prev: Position[], next: Position[]) => prev.concat(next), []);

    coordinates.forEach((coordinates) => getCoordinatesBorders(coordinates, borders));
    return borders;
}

function calculateCenter(geometries: Geometry[]) {
    const borders = getGeometriesBorders(geometries);

    const x = borders.left + (borders.right - borders.left) / 2;
    const y = borders.top + (borders.bottom - borders.top) / 2;

    return { center: [x, y], bbox: [Math.abs(borders.left - borders.right), Math.abs(borders.top - borders.bottom)] };
}

function getMapProjection(mapPresent: PresentState) {
    return mapPresent.projection === Projection.EPSG_900913 ? Projection.EPSG_3857 : mapPresent.projection;
}

const getMaxResolution = (layers: Layer[], selectedLayerNames: string[]) => {
    const maxResolution = Math.min(
        ...layers
            .filter((layer) => selectedLayerNames.includes(layer.name) && layer.maxResolution)
            .map((layer: Layer) => layer.maxResolution)
    );
    return maxResolution;
};

const calculateMinAllowedZoom = (selectedLayerNames: string[], layers?: Layer[]) => {
    let zoom = undefined;
    if (isNotEmptyArray(selectedLayerNames) && isNotEmptyArray(layers)) {
        const maxResolution = getMaxResolution(layers, selectedLayerNames);
        if (isNumber(maxResolution)) {
            zoom = getZoomFromResolution(maxResolution) + 1;
        }
    }
    return zoom;
};

function calculateZoom(
    mapPresent: PresentState,
    targetScreenRatio: number,
    ratioX: number,
    ratioY: number,
    features: Feature[],
    layers: Layer[] | undefined
) {
    let targetZoom = Math.min(
        mapPresent.zoom + Math.ceil(Math.log2(targetScreenRatio * Math.min(ratioX, ratioY))),
        MAX_ZOOM
    );
    const selectedLayers: string[] = features
        ? features.map((feature) => feature.properties.layer).filter((layer): layer is string => layer !== undefined)
        : [];
    const minAllowedZoom = calculateMinAllowedZoom(selectedLayers, layers);
    if (minAllowedZoom && minAllowedZoom > targetZoom) {
        targetZoom = minAllowedZoom;
    }
    return targetZoom;
}

export function calculateFeatureCenter(features?: Feature[], mapPresent?: PresentState, layers?: Layer[]) {
    if (!features || !mapPresent?.bbox) {
        throw new Error("features || mapPresent.bbox is undefined");
    }
    const featuresWithNullGeometry = features.filter((feature) => !feature.geometry).map((feature) => feature.id);

    if (featuresWithNullGeometry.length > 0) {
        logger.warn(`features with null geometry: ${featuresWithNullGeometry}`);
    }

    const centerCalculation = calculateCenter(features.map((feature) => feature.geometry));

    const targetScreenRatio =
        (TARGET_SCREEN_RATIO_MIN - TARGET_SCREEN_RATIO_MAX) / features.length + TARGET_SCREEN_RATIO_MAX;

    const mapBbox = mapPresent?.bbox.bounds;
    const mapBboxSize = [Math.abs(mapBbox.maxx - mapBbox.minx), Math.abs(mapBbox.maxy - mapBbox.miny)];

    const ratioX = mapBboxSize[0] / centerCalculation.bbox[0];
    const ratioY = mapBboxSize[1] / centerCalculation.bbox[1];

    let targetZoom = calculateZoom(mapPresent, targetScreenRatio, ratioX, ratioY, features, layers);

    return {
        center: reproject(centerCalculation.center, Projection.EPSG_3857, getMapProjection(mapPresent)),
        zoom: Math.min(targetZoom, MAX_ZOOM),
    };
}
